import { AdminUser } from 'src/types/shared/AdminUser';
import { site } from 'lib/src/shared/enums/site';
import { Tag } from './Tags';
import ScoreWeighting from 'lib/src/types/shared/ScoreWeighting';
import { AssessmentSummary } from './Assessments';

export interface Challenge {
    id: number;
    challengeGroupID: number;
    name: string;
    publishDate: Date | string | null;
    startDate: Date | string | null;
    endDate: Date | string;
    gracePeriod: number;
    awardDescription: string;
    background: string;
    imageS3Key: string | null;
    partnerLogoS3Key: string | null;
    partnerSecondLogoS3Key: string | null;
    partnerThirdLogoS3Key: string | null;
    summaryImage1S3Key: string | null;
    summaryImage2S3Key: string | null;
    responseDate: Date | string;
    challengeLeadID: number | null;
    challengeLead: AdminUser | null;
    workbooksCampaignID: string;
    scoreWeightingID: number;
    readyForAssessmentsSetOn: Date | null;
    assessmentsCompleteSetOn: Date | null;
    isDeleted: boolean;
    site: site;
    descriptionSections: ChallengeDescriptionSection[] | null;
    challengeTags: Tag[];
    businessTags: Tag[];
    scoreWeighting: ScoreWeighting | null;
    workshopDate: Date | string;
    workshopAdminIDs: number[];
    assessmentStartDate: Date | string;
    assessorIDs: number[];
    pitchPresentationDate: Date | string;
    pitchPresentationAdminIDs: number[];
    challengeStage: keyof ChallengeStage;
    applicationSnapshot: ChallengeApplicationsSnapshot[];
    previewKey: string;
}

export interface ChallengeApplicationsSnapshot {
    percentComplete: number;
    count: number;
}

export interface CalendarChallenge {
    id: number;
    site: site;
    challengeGroupID: number;
    name: string;
    workshopDate: Date | string;
    startDate: Date | string;
    endDate: Date | string;
    gracePeriod: number;
    assessmentStartDate: Date | string;
    responseDate: Date | string;
    pitchPresentationDate: Date | string;
    challengeLeadID: number;
    workshopAdminIDs: number[];
    assessorIDs: number[];
    pitchPresentationAdminIDs: number[];
    challengeStage: 10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90;
}

export interface ChallengeDescriptionSection {
    id: number;
    challengeID: number;
    sort: number;
    title: string;
    contents: ChallengeDescriptionSectionContent[];
    type: DescriptionSectionTypes;
}

export interface ChallengeDescriptionSectionContent {
    id: number;
    sectionID: number;
    content: string;
    title: string;
    sort: number;
}

export interface ChallengeDescriptionSectionRequest {
    type: DescriptionSectionTypes;
    title: string;
    sort: number;
    contents: ChallengeDescriptionSectionContentRequest[];
}

export interface ChallengeDescriptionSectionContentRequest {
    sort: number;
    content: string;
    title: string;
}

export enum DescriptionSectionTypes {
    WYSIWYG = 0,
    Accordion = 1,
    Video = 2,
    Image = 3,
}

export const DescriptionSectionTypeValues = {
    [DescriptionSectionTypes.WYSIWYG]: 'WYSIWYG',
    [DescriptionSectionTypes.Accordion]: 'Accordion',
    [DescriptionSectionTypes.Video]: 'Video',
    [DescriptionSectionTypes.Image]: 'Image',
};

export interface SelectUser {
    id: number;
    firstName: string;
    lastName: string;
    userCurrentlyHoldsRole: boolean;
}

export type ChallengeAssessor = SelectUser;
export type ChallengeLead = SelectUser;

export interface ChallengeScoreSummary {
    challengeID: number;
    challenge: Challenge;
    challengeGroup: number | null;
    expiryDate: string;
    token: string | null;
    assessmentSummaries: AssessmentSummary[];
}

export interface ChallengeApplicationResponse {
    id: number;
    challengeID: number;
    challengeName: string;
    userID: number;
    createdOn: string | null;
    isDeleted: boolean;
    companyName: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    townCity: string | null;
    postcode: string | null;
    county: string | null;
    region: string | null;
    url: string | null;
    companyReg: string | null;
    turnover: number | null;
    contactName: string | null;
    jobTitle: string | null;
    email: string | null;
    telephone: string | null;
    isCompanyInfoComplete: boolean;
    ideaSummary: string | null;
    isSummaryComplete: boolean;
    trlDescription: string | null;
    trl: number | null;
    isTRLComplete: boolean;
    ipStatusDescription: string | null;
    isIPStatusComplete: boolean;
    isExtraAttachmentsComplete: boolean;
    submittedOn: string | null;
    ktnEntrySummary: string | null;
    overallScoreSummary: string | null;
    hasChallengeGroupPermissions: boolean;
    hasUserAssessed: boolean;
    attachmentS3Keys: string[] | null;
    expiryDate: string | null;
    token: string | null;
}

export interface FormattedDate {
    y: string;
    m: string;
    d: string;
}

export interface DateItem {
    name: string;
    value: number;
}

export interface LookupObject {
    from: FormattedDate;
    to: FormattedDate;
}

export const challengeStageTypes: string[] = ['workshop', 'livechallenge', 'assessment', 'pitch'];
export const challengeStageNames: string[] = ['Workshop', 'Live Challenge', 'Assessment', 'Pitch'];

export enum ChallengeStage {
    PendingWorkshop = 10,
    Workshop = 20,
    PendingChallengeStart = 30,
    LiveChallenge = 40,
    PendingAssessment = 50,
    Assessment = 60,
    PendingPitch = 70,
    Pitch = 80,
    Closed = 90,
}

export const challengeStageValues: Record<string, string> = {
    [`${ChallengeStage.PendingWorkshop}`]: 'Pending Workshop',
    [`${ChallengeStage.Workshop}`]: 'Workshop',
    [`${ChallengeStage.PendingChallengeStart}`]: 'Pending Challenge Start',
    [`${ChallengeStage.LiveChallenge}`]: 'Live Challenge',
    [`${ChallengeStage.PendingAssessment}`]: 'Pending Assessment',
    [`${ChallengeStage.Assessment}`]: 'Assessment',
    [`${ChallengeStage.PendingPitch}`]: 'Pending Pitch',
    [`${ChallengeStage.Pitch}`]: 'Pitch',
    [`${ChallengeStage.Closed}`]: 'Closed',
};

export interface CalendarOptionResponse {
    allChallengeGroups: CalendarChallengeGroupOption[];
    myChallengeGroups: CalendarChallengeGroupOption[];
}
export interface CalendarChallengeGroupOption {
    challengeGroupID: number;
    challengeGroupName: string;
}

export interface CalendarChallengeGroupDropdownOption {
    value: number;
    label: string;
}
